import { createContext, useCallback, useContext } from 'react'
import { supabase } from '@/lib/supabase'
import { useIsOperator } from './useIsOperator'
import { useScopedRead } from './useScopedRead'

/**
 * The shell shares one acknowledgement between the route and both menus.
 *
 * `operatorSetup` is the organisation's supplier-tools switch (`offers_enabled`). An operator
 * sets up NEW destination sites only where that module is on; an organisation that already has
 * a site keeps it either way. Before 28.09.2026 every operator saw «Destinasjoner», so Nortura
 * PROFF, with Tilbud and Katalog switched off, still had a venue tool in its menu.
 */
export function useDestinationAccessReader(activeOrgId: string | null, operatorSetup = true) {
  const { isOperator, loading: operatorLoading } = useIsOperator()
  const operatorGrant = isOperator && operatorSetup
  const resource = !operatorGrant && !operatorLoading && activeOrgId
    ? `destination-access:${activeOrgId}`
    : null
  const read = useCallback(async () => {
    if (!activeOrgId) throw new Error('destination_organization_required')
    // Existing RLS owns delegated site membership. This grants no SMS/offer access.
    const { data, error } = await supabase.from('destination_sites')
      .select('id').eq('org_id', activeOrgId).limit(1)
    if (error) throw error
    if (!data) throw new Error('destination_access_unconfirmed')
    return data.length > 0
  }, [activeOrgId])
  const access = useScopedRead(resource, read)
  return {
    isOperator,
    canAccessDestination: operatorGrant || (!access.loading && !access.error && access.data === true),
    loading: operatorLoading || access.loading,
    error: access.error,
    retry: access.reload,
  }
}

const DestinationAccessContext = createContext<ReturnType<typeof useDestinationAccessReader> | null>(null)
export const DestinationAccessProvider = DestinationAccessContext.Provider

export function useDestinationAccess() {
  const access = useContext(DestinationAccessContext)
  if (!access) throw new Error('DestinationAccessProvider required')
  return access
}
